/**
 * Thin wrapper over the two `settings-transfer:*` IPC channels.
 *
 * The `localStorage` test overrides mirror `queryFilesApi.openSqlFile` — e2e
 * cannot click a native dialog, so in test mode the renderer supplies the path
 * and main skips the dialog. Both overrides are gated on
 * `electronAPI.testMode.isTestMode()`, so they are inert in a shipped build.
 */
import type {
  SettingsTransferExportResult,
  SettingsTransferImportResult,
} from '../../../types/settingsTransfer';

const TEST_EXPORT_PATH_KEY = 'sql-studio-test-settings-export-file';
const TEST_IMPORT_PATH_KEY = 'sql-studio-test-settings-import-file';

/**
 * The honest caveat, shown wherever MetaInfo is offered. Two things it must
 * say, because a feature whose whole premise is "you can see what's in the
 * file" cannot be vague about either:
 *
 *  1. **It is not only declarations.** `EnumValueCache.values` holds up to 200
 *     distinct values SAMPLED FROM THE COLUMN, and the JSON structure cache
 *     holds paths derived from real documents. Calling the whole thing
 *     "declarations" — as an earlier draft of this string, the docs page and
 *     the neighbouring reassurance line all did — understated it.
 *  2. Records re-attach by connection id, which is generated per machine, so
 *     a bundle imported next to different connections attaches to nothing.
 *     Told plainly because the alternative (an id-mapping UI) is a feature of
 *     its own and this task does not ship one.
 */
export const METAINFO_CAVEAT =
  'Loose foreign keys, JSON, enum and spatial declarations for every database — ' +
  'including cached sample values read from your columns and JSON paths taken ' +
  'from real documents, so this group can contain data, not just definitions. ' +
  'Records re-attach by connection id, so they only line up if the target ' +
  'machine has the same connections — otherwise they import but stay dormant.';

function testOverridePath(key: string): string | undefined {
  if (!window.electronAPI?.testMode?.isTestMode?.()) return undefined;
  if (typeof localStorage === 'undefined') return undefined;
  return localStorage.getItem(key)?.trim() || undefined;
}

export async function exportBundleToFile(args: {
  json: string;
  suggestedFileName: string;
}): Promise<SettingsTransferExportResult> {
  if (!window.electronAPI?.settingsTransfer?.exportBundle) {
    return { success: false, error: 'Exporting settings is unavailable here.' };
  }
  const filePath = testOverridePath(TEST_EXPORT_PATH_KEY);
  return window.electronAPI.settingsTransfer.exportBundle({
    json: args.json,
    suggestedFileName: args.suggestedFileName,
    ...(filePath ? { filePath } : {}),
  });
}

export async function importBundleFromFile(): Promise<SettingsTransferImportResult> {
  if (!window.electronAPI?.settingsTransfer?.importBundle) {
    return { success: false, error: 'Importing settings is unavailable here.' };
  }
  const filePath = testOverridePath(TEST_IMPORT_PATH_KEY);
  return window.electronAPI.settingsTransfer.importBundle(filePath ? { filePath } : {});
}

/** `metainfo:list-all` — the only main-process source an export ever reads. */
export async function listAllMetaInfo(): Promise<Array<Record<string, unknown>>> {
  const records = await window.electronAPI?.metainfo?.listAll?.();
  return (records ?? []) as unknown as Array<Record<string, unknown>>;
}

export async function importMetaInfoRecord(
  connectionId: string,
  database: string,
  json: string,
  mode: 'merge' | 'replace',
): Promise<void> {
  await window.electronAPI?.metainfo?.import?.(connectionId, database, json, mode);
}
